import React from 'react'
import * as rn from 'react-native'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import * as c from '../../../common'
import * as r from '../../../react-utils'
import { processErr } from '../../../common'

import * as Mui from '@mui/material'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import FormControl from '@mui/material/FormControl'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import InputLabel from '@mui/material/InputLabel'
import OutlinedInput from '@mui/material/OutlinedInput'
import TextField from '@mui/material/TextField'
import { Visibility, VisibilityOff } from '@mui/icons-material'

import * as Fire from '../../fire'
import Pad from '../../comps/Pad'

//@ts-ignore
import styles from './Login.module.css'

export interface LoginProps {}

const logo = c.LOGO_BIG
const logoDark = c.LOGO_BIG_DARK || c.LOGO_BIG

export default React.memo<LoginProps>(function Login() {
  const [authenticating, setAuthenticating] = React.useState(false)
  const [email, setEmail] = React.useState('')
  const [pass, setPass] = React.useState('')
  const [err, setErr] = React.useState('')

  const t = r.useTheme()
  const theme = Mui.useTheme()
  const location = useLocation()
  const navigate = useNavigate()

  const colorScheme = rn.useColorScheme()
  const isDark = theme.palette.mode === 'dark' || colorScheme === 'dark'

  const [isLoad, setIsLoad] = React.useState(false)

  const [showPassword, setShowPassword] = React.useState(false)

  const from = (location.state as any)?.from?.pathname || '/'

  const resetError = React.useCallback(() => {
    setErr('')
  }, [])

  const handleEmailChange = React.useCallback(
    (e: { target: { value: string } }) => {
      setEmail(e.target.value)
    },
    [],
  )

  const handlePassChange = React.useCallback(
    (e: { target: { value: string } }) => {
      setPass(e.target.value)
    },
    [],
  )

  const handleSubmit = React.useCallback(async () => {
    try {
      setErr('')
      setAuthenticating(true)
      await Fire.signInWithEmailAndPassword(email, pass)
      navigate(from, { replace: true })
    } catch (e) {
      setErr(processErr(e))
      setAuthenticating(false)
    }
  }, [email, from, navigate, pass])

  React.useEffect(() => {
    window.addEventListener('keyup', (e) => {
      if (e.key === 'Enter' && email !== '' && pass !== '') {
        handleSubmit()
      }
    })
  }, [email, pass, handleSubmit])

  React.useEffect(() => {
    setIsLoad(true)
  }, [isLoad, setIsLoad])

  const handleClickShowPassword = React.useCallback(
    () => setShowPassword((show) => !show),
    [],
  )

  const handleMouseDownPassword = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault()
    },
    [],
  )

  const visibilitySx = React.useMemo(
    (): Mui.SxProps => ({
      color: t.canvas.color as string,
    }),
    [t.canvas.color],
  )

  const endAdornmentPassVisible = React.useMemo(
    () => (
      <InputAdornment position="end">
        <IconButton
          aria-label="toggle password visibility"
          edge="end"
          onClick={handleClickShowPassword}
          onMouseDown={handleMouseDownPassword}
        >
          <VisibilityOff sx={visibilitySx} />
        </IconButton>
      </InputAdornment>
    ),
    [handleClickShowPassword, handleMouseDownPassword, visibilitySx],
  )

  const endAdornmentPassHidden = React.useMemo(
    () => (
      <InputAdornment position="end">
        <IconButton
          aria-label="toggle password visibility"
          edge="end"
          onClick={handleClickShowPassword}
          onMouseDown={handleMouseDownPassword}
        >
          <Visibility sx={visibilitySx} />
        </IconButton>
      </InputAdornment>
    ),
    [handleClickShowPassword, handleMouseDownPassword, visibilitySx],
  )

  const inputProps = React.useMemo(
    (): Mui.InputBaseProps['inputProps'] => ({
      style: {
        ...t.input.canvas,
        //@ts-expect-error TO-DO: Mui will be removed
        backgroundColor: t.input.canvas.backgroundColor,
        borderWidth: 1,
      },
    }),
    [t.input.canvas],
  )

  const inputLabelProps = React.useMemo(
    (): Partial<Mui.InputLabelProps> => ({
      sx: {
        color: t.input.canvas.labelColor,
        fontFamily: t.input.canvas.labelFontFamily,
      },
    }),
    [t.input.canvas.labelColor, t.input.canvas.labelFontFamily],
  )

  const btnSx = React.useMemo(
    () => ({
      backgroundColor: t.button.canvas.backgroundColor as string,
      color: t.button.canvas.color as string,
      fontFamily: t.fontFamily,
    }),
    [t.button.canvas.backgroundColor, t.button.canvas.color, t.fontFamily],
  )
  const btnSxSubmit = React.useMemo(
    () => ({
      ...btnSx,
      width: '100%',
    }),
    [btnSx],
  )

  if (err) {
    return (
      <Box className={styles['container']}>
        <Alert severity="error" sx={{ maxWidth: '300px' }}>
          {err}
        </Alert>
        <Pad amt={10} />
        <Button onClick={resetError} sx={btnSx} variant="contained">
          Go Back
        </Button>
      </Box>
    )
  }

  return (
    <Box className={styles['container']}>
      <Box className={styles['login-box']}>
        <div className={styles['brand']}>
          <img alt="Logotype" src={isDark ? logoDark : logo} width={200} />
        </div>

        <Pad amt={30} />

        <Grid container alignItems="center">
          <Grid item xs={12}>
            <TextField
              InputLabelProps={inputLabelProps}
              className={styles['input']}
              disabled={authenticating}
              inputProps={inputProps}
              id="email"
              label="Email"
              onChange={handleEmailChange}
              type="email"
              value={email}
            />
          </Grid>

          <Grid item xs={12}>
            <Pad amt={24} />
          </Grid>

          <Grid item xs={12}>
            <FormControl className={styles['form-control']} variant="outlined">
              <InputLabel
                {...inputLabelProps}
                htmlFor="outlined-adornment-password"
              >
                Password
              </InputLabel>
              <OutlinedInput
                className={styles['input']}
                disabled={authenticating}
                endAdornment={
                  showPassword
                    ? endAdornmentPassVisible
                    : endAdornmentPassHidden
                }
                id="outlined-adornment-password"
                inputProps={inputProps}
                label="Password"
                onChange={handlePassChange}
                type={showPassword ? 'text' : 'password'}
                value={pass}
              />
            </FormControl>
          </Grid>
        </Grid>
        <Pad amt={30} />
        <Mui.Typography fontFamily={t.canvas.fontFamily}>
          Version: {c.processEnv['REACT_APP_APP_VERSION']} (#
          {c.processEnv['REACT_APP_COMMIT_SHORT_HASH']})
        </Mui.Typography>

        <Pad amt={30} />

        <Button sx={btnSxSubmit} onClick={handleSubmit} variant="contained">
          Login
        </Button>

        <Pad amt={48} />

        <Link to="/privacy-policy">
          <Mui.Typography fontFamily={t.fontFamily}>
            Privacy Policy
          </Mui.Typography>
        </Link>

        <a href="itms-services://?action=download-manifest&url=https://www.ecohomereports.com/manifest.plist">
          <Mui.Typography fontFamily={t.fontFamily}>
            Install iOS app
          </Mui.Typography>
        </a>

        {/* <div className={styles['options']}>
          <label className={styles['remember-pass']} htmlFor="remember-pass">
            Remember me
            <input id="remember-pass" type="checkbox" />
          </label>

          <Pad amt={12} />

          <div className={styles['submit-or-forgot']}>
            

            <a className={styles['forgot-pass']} href="#">
              Forgot your password?
            </a>
          </div>
        </div> */}
      </Box>
    </Box>
  )
})

export const rootBGLogin: c.Surface = 'backdrop'

export interface CredentialResponse {
  aud: string
  azp: string
  email_verified: boolean
  email: string
  exp: number
  family_name: string
  given_name: string
  iat: number
  iss: string
  jti: string
  name: string
  nbf: number
  picture: string
  sub: string
}

export interface GoogleUser {
  email: string
}
